import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'outline' | 'ghost' | 'accent';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  icon?: string;
  children?: ReactNode;
}

const variantClass: Record<Variant, string> = {
  primary:
    'bg-primary-600 text-background-50 hover:bg-primary-700 dark:text-foreground-950',
  secondary:
    'bg-secondary-100 text-secondary-900 hover:bg-secondary-200 border border-secondary-200',
  outline:
    'border border-background-300 bg-background-50 text-foreground-800 hover:border-primary-400 hover:text-primary-700',
  ghost: 'text-foreground-700 hover:bg-background-100',
  accent: 'bg-accent-500 text-background-50 hover:bg-accent-600 dark:text-foreground-950',
};

const sizeClass: Record<Size, string> = {
  sm: 'h-8 px-3 text-xs gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-6 text-sm gap-2',
};

export default function Button({
  variant = 'primary',
  size = 'md',
  icon,
  children,
  className = '',
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      className={`inline-flex items-center justify-center whitespace-nowrap rounded-md font-medium transition-colors duration-200 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed ${variantClass[variant]} ${sizeClass[size]} ${className}`}
    >
      {icon ? <i className={`${icon} text-base leading-none`} /> : null}
      {children}
    </button>
  );
}
